/* eslint-disable react/prop-types */
/**
 * Phase 19 — placement utilities (WS-D, CP4).
 *
 * Shared between BattlespaceMap3D and PlacementXYPane so both modes
 * write the SAME placement-block shape. The round-trip commute
 * contract lives here: ``upsertEntry`` is mode-agnostic; only the
 * ``source_mode`` field on the block changes between writers.
 */

export const DEFENCE_PALETTE = Object.freeze({
    navy: "#0B2545",
    steelGray: "#5A6B7C",
    teal: "#4FB6C6",
});

// Salisbury Plain centre (51.18° N, -1.80° W) at zoom 11 — the
// Phase 19 demo site. customer site coordinates are pending the
// scoping-workshop confirmation; the UI labels its tab as such.
export const DEFAULT_VIEW_STATE = Object.freeze({
    latitude: 51.18,
    longitude: -1.80,
    zoom: 11,
    pitch: 30,
    bearing: 0,
});

const ASSET_COLOURS = Object.freeze({
    radar: "#4FB6C6",          // teal accent — sensor coverage
    battery: "#A8C4D6",        // light steel-blue — own forces (contrasts
                               // against the dark map background; the
                               // ICON glyph body keeps the navy fill so
                               // the asset type is still navy-coded).
    threat_instance: "#D14D5F",// muted defence-red for inbound threats
});

export function assetTypeColour(kind, rgba = false) {
    const hex = ASSET_COLOURS[kind] || DEFENCE_PALETTE.steelGray;
    if (!rgba) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b, 220];
}

export function iconSvgDataUri(kind) {
    // Minimal inline SVG glyphs in defence palette. Returning a data
    // URI keeps the icons inside the JS bundle (no separate HTTP).
    const colour = ASSET_COLOURS[kind] || DEFENCE_PALETTE.steelGray;
    let body;
    if (kind === "radar") {
        body = `<circle cx='24' cy='24' r='10' fill='${colour}'/>` +
               `<circle cx='24' cy='24' r='16' fill='none' stroke='${colour}' stroke-width='2'/>` +
               `<circle cx='24' cy='24' r='22' fill='none' stroke='${colour}' stroke-width='1.2' stroke-dasharray='3 3'/>`;
    } else if (kind === "battery") {
        body = `<rect x='14' y='14' width='20' height='20' rx='3' ` +
               `fill='${colour}' stroke='${DEFENCE_PALETTE.teal}' stroke-width='2'/>` +
               `<rect x='18' y='10' width='4' height='6' fill='${DEFENCE_PALETTE.teal}'/>` +
               `<rect x='26' y='10' width='4' height='6' fill='${DEFENCE_PALETTE.teal}'/>`;
    } else {
        body = `<polygon points='24,10 38,38 10,38' fill='${colour}'/>` +
               `<polygon points='24,18 32,34 16,34' fill='${DEFENCE_PALETTE.navy}' opacity='0.35'/>`;
    }
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'>${body}</svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Convert a Placement to a flat feature list for Deck.gl.
 */
export function placementToFeatures(placement) {
    if (!placement || !Array.isArray(placement.entries)) return [];
    return placement.entries.map((entry) => ({
        asset_id: entry.asset_id,
        asset_type: entry.asset_type,
        lat: entry.position?.lat ?? 0,
        lon: entry.position?.lon ?? 0,
        alt_m: entry.position?.alt_m ?? 0,
    }));
}

/**
 * Insert-or-update an entry in a placement block. Returns a new
 * Placement object; the caller is responsible for storing it.
 *
 * ``sourceMode`` records which UI surface wrote this version of
 * the block (informational only; the data shape is identical
 * across modes).
 */
export function upsertEntry(placement, entry, { sourceMode = null } = {}) {
    const prev = placement?.entries || [];
    const next = [...prev];
    const i = next.findIndex((e) => e.asset_id === entry.asset_id);
    if (i >= 0) {
        next[i] = { ...next[i], ...entry, position: { ...entry.position } };
    } else {
        next.push({ ...entry, position: { ...entry.position } });
    }
    return {
        entries: next,
        source_mode: sourceMode ?? placement?.source_mode ?? null,
    };
}
